<template>
  <div>
    <invoice-send-modal ref="invoiceSendModal" @sent="refreshInvoice"/>
    <invoice-payment-modal ref="invoicePaymentModal" @updatedInvoice="handleInvoiceUpdate" :includes="includes"/>

    <q-toolbar color="white" text-color="grey-9" class="top-page-toolbar">
      <q-toolbar-title v-if="invoice">
        <strong>{{ $tc('invoice', 1) }}</strong> #{{ invoice.invoice_number }}

        <status-chip
          small
          class="on-right"
          :status="invoice.status"
          :committed="isCommitted"
          :color="statusColor"
          :clickable="user.isUserOfActiveTeam()"
          @cancel="handleCancelInvoice(invoice.id)"
          @paid="handleFullPayment"
          @open="handleMarkInvoiceAsOpen"
        />
      </q-toolbar-title>

      <icon-bar :items="iconBarItems"></icon-bar>
    </q-toolbar>

    <div class="layout-padding" :class="{ 'q-pt-none': isCanceled || isCancellationInvoice }">
      <div v-if="invoice && (isCanceled || isCancellationInvoice)" class="row justify-center q-px-md q-py-lg">
        <q-btn-toggle
          v-model="view"
          toggle-color="dark"
          class="invoice__storno-toggle"
          outline
          :options="[
            { label: $t('original'), value: 'invoice' },
            { label: $t('cancel_invoice_title'), value: 'storno' },
          ]"
        />
      </div>

      <div v-if="invoice" class="row gutter-sm">
        <div class="col-12 col-lg-6 col-xl-7">
          <document-viewer :document="document"/>
        </div>

        <div class="col-12 col-lg-6 col-xl-5">
          <q-tabs color="tertiary" underline-color="white">
            <q-tab slot="title" :label="$t('invoice_meta')" name="meta" default />
            <q-tab v-if="!isCancellationInvoice" slot="title" :label="$tc('payment', 2)" name="payment" />
            <q-tab slot="title" :label="$tc('note', 2)" name="notes" />

            <q-tab-pane name="meta" class="bg-white">
              <q-item class="invoice__invoice-date-entry">
                <q-item-main :label="invoiceDate" :sublabel="$t('invoice_date')"/>
              </q-item>

              <q-item v-if="deliveryDate" class="invoice__invoice-date-entry">
                <q-item-main :label="deliveryDate" :sublabel="$t('delivery_date')"/>
              </q-item>

              <q-item v-if="dueDate" class="invoice__due-date-entry">
                <q-item-main :label="dueDate" :sublabel="$t('due_date')"/>
              </q-item>

              <q-item v-if="cashDiscountLimitDate" class="invoice__due-date-entry">
                <q-item-main :label="cashDiscountLabel" :sublabel="$t('cash_discount')"/>
              </q-item>

              <q-item v-if="createdBy" class="invoice__due-date-entry">
                <q-item-main
                  :label="[createdBy.first_name,createdBy.last_name].join(' ')"
                  :sublabel="$t('invoice_created_by')"
                />
              </q-item>

              <q-item class="invoice__customer-name-entry">
                <q-item-main>
                  <q-item-tile label>
                    <router-link
                      :to="{ name: 'customer', params: { customerId: customer.id, teamId: activeTeam.id } }"
                      class="router-link"
                      v-if="invoice.customer_exists"
                    >
                      {{ customerName }}
                    </router-link>
                    <div v-else>
                      {{ customerName }}
                    </div>
                  </q-item-tile>

                  <q-item-tile sublabel>
                    {{ $tc('customer', 1) }}
                  </q-item-tile>
                </q-item-main>
              </q-item>

              <q-item class="invoice__due-date-entry" v-if="contactPersonName">
                <q-item-main :label="contactPersonName" :sublabel="$tc('contact_person', 1)"/>
              </q-item>

              <q-item v-if="invoice.invoice_address" class="invoice__invoice-address-entry">
                <q-item-main>
                  <q-item-tile v-html="invoice.invoice_address.data.formatted_address" label/>

                  <q-item-tile sublabel>
                    {{ $t('invoice_address') }}
                  </q-item-tile>
                </q-item-main>
              </q-item>

              <q-item class="invoice__customer-name-entry" v-if="invoice.quote_id">
                <q-item-main>
                  <q-item-tile label>
                    <router-link
                      :to="{ name: 'quote', params: { quote: invoice.quote_id, teamId: activeTeam.id } }"
                      class="router-link">
                      {{ invoice.quote.data.quote_number }}
                    </router-link>
                  </q-item-tile>

                  <q-item-tile sublabel>
                    {{ $tc('quote', 1) }}
                  </q-item-tile>
                </q-item-main>
              </q-item>

              <q-item class="invoice__project-entry" v-if="invoice.project_id">
                <q-item-main>
                  <q-item-tile label>
                    <router-link
                      :to="{ name: 'project', params: { project: invoice.project_id, teamId: activeTeam.id } }"
                      class="router-link">
                      {{ invoice.project.data.name }}
                    </router-link>
                  </q-item-tile>

                  <q-item-tile sublabel>
                    {{ $tc('project', 1) }}
                  </q-item-tile>
                </q-item-main>
              </q-item>
            </q-tab-pane>

            <q-tab-pane v-if="!isCancellationInvoice" name="payment" class="bg-white">
              <q-item v-if="showPaymentsButton" class="invoice__invoice-payment-button">
                <q-item-main>
                  <q-btn-dropdown
                    @click="handleFullPayment"
                    :label="$t('mark_paid')"
                    split
                    outline
                  >
                    <q-list link>
                      <q-item @click.native="handleAddPayment" v-close-overlay>
                        <q-item-main>
                          <q-item-tile label>{{ $t('add_payment') }}</q-item-tile>
                        </q-item-main>
                      </q-item>
                    </q-list>
                  </q-btn-dropdown>
                </q-item-main>
              </q-item>

              <q-item v-for="payment in payments" :key="payment.id" class="invoice__invoice-payment-entry">
                <q-item-main>
                  <q-item-tile label>
                    {{ payment.balance | money(invoice.currency) }}
                  </q-item-tile>

                  <q-item-tile sublabel>
                    {{ payment.payment_date.date | date($t('date_format')) }}
                    <span v-if="payment.payment_method"> &#183; {{ $t(payment.payment_method) }}</span>
                  </q-item-tile>
                </q-item-main>

                <q-item-side right>
                  <q-btn @click="handleDeletePayment(payment)" v-is-user icon="close" flat dense/>
                </q-item-side>
              </q-item>

              <q-item v-if="!payments.length" class="text-italic row justify-center q-mt-lg">
                {{ $t('payments_empty') }}
              </q-item>
            </q-tab-pane>

            <q-tab-pane name="notes" class="bg-white">
               <note-input
                  :notes="notes"
                  :createNoteMethod="handleCreateNote"
                  :updateNoteMethod="handleUpdateNote"
                  :deleteNoteMethod="handleDeleteNote"
                  hideEmptyState
                  style="height: 250px"
                />
            </q-tab-pane>
            <q-tab-pane name="tab-5">Tab Five</q-tab-pane>
          </q-tabs>

          <q-card v-if="activities" class="bg-white q-mt-lg">
            <q-card-main>
              <timeline :activities="activities" />
            </q-card-main>
          </q-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { formatMoney, formatPercentage } from 'src/utils/format'
  import { mapActions, mapGetters } from 'vuex'
  import DocumentViewer from 'components/Documents/DocumentViewer.vue'
  import FileSaver from 'file-saver'
  import IconBar from 'components/Common/IconBar'
  import { InvoiceClient } from 'src/api/Invoices'
  import InvoicePaymentModal from 'components/Invoices/InvoicePaymentModal.vue'
  import InvoiceSendModal from 'components/Invoices/InvoiceSendModal.vue'
  import { NoteClient } from 'src/api/Notes'
  import { NoteInput } from 'components/Common/Inputs/NoteInput'
  import StatusChip from 'components/Invoices/StatusChip'
  import Timeline from 'components/Invoices/Timeline.vue'
  import { createErrorFromResponse } from 'src/utils/error'
  import { date } from 'quasar'
  import { getCustomerName } from 'src/utils/searchResultParser'
  import handleCancelInvoice from 'src/components/Invoices/Utils/cancel-invoice'
  import { invoices as invoicesConfig } from 'src/config'
  import parseDate from 'date-fns/parse'
  import printJS from 'print-js'
  import reopenInvoice from 'src/utils/reopenInvoice'

  const INCLUDES = ['user', 'customer', 'invoice_address', 'payments', 'quote', 'notes.user', 'documents', 'project', 'canceled_by', 'canceled_by.documents', 'activities', 'contact_person']

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    data () {
      return {
        invoice: null,
        pdf: null
      }
    },

    computed: {
      ...mapGetters(['activeTeam', 'user']),

      contactPersonName () {
        if (this.invoice.contact_person) {
          return [this.invoice.contact_person.data.first_name, this.invoice.contact_person.data.last_name].join(' ')
        }
      },

      createdBy () {
        if (!this.invoice.user) {
          return null
        }

        return this.invoice.user.data
      },

      includes () {
        return INCLUDES
      },

      invoiceDate () {
        return date.formatDate(parseDate(this.invoice.invoice_date.date), this.$t('date_format'))
      },

      dueDate () {
        if (this.invoice.due_date) {
          return date.formatDate(parseDate(this.invoice.due_date.date), this.$t('date_format'))
        }
      },

      deliveryDate () {
        if (this.invoice.delivery_date) {
          return date.formatDate(parseDate(this.invoice.delivery_date.date), this.$t('date_format'))
        }
      },

      cashDiscountLimitDate () {
        if (this.invoice.cash_discount_limit) {
          return date.formatDate(parseDate(this.invoice.cash_discount_limit.date), this.$t('date_format'))
        }
      },

      cashDiscountLabel () {
        return this.$t('cash_discount_payment_until_label', {
          cashDiscount: formatPercentage(this.invoice.cash_discount / 100, 0, 2),
          cashDiscountLimit: this.cashDiscountLimitDate
        })
      },

      customer () {
        return this.invoice.customer.data
      },

      customerName () {
        return getCustomerName(this.customer)
      },

      payments () {
        // slice() is there to not mutate the original array
        return this.invoice.payments.data.slice().sort((a, b) => {
          if (a.payment_date.date === b.payment_date.date) {
            return a.created_at.date <= b.created_at.date
          } else {
            return a.payment_date.date <= b.payment_date.date
          }
        })
      },

      showPaymentsButton () {
        return !['paid', 'paid_cash_discount'].includes(this.invoice.status)
      },

      hasNotes () {
        return this.invoice.hasOwnProperty('notes')
      },

      notes () {
        return this.hasNotes ? this.invoice.notes.data : []
      },

      statusColor () {
        return invoicesConfig.status_colors[this.invoice.status]
      },

      document () {
        const result = this.invoice.documents.data.find((document) => document.type === 'with_template')

        if (!result) {
          return this.invoice.documents.data[0]
        } else {
          return result
        }
      },

      iconBarItems () {
        let items = [
          {
            text: 'send_as_mail',
            icon: 'email',
            action: this.handleSend,
            restricted: true
          },
          {
            text: 'download_pdf',
            icon: 'cloud_download',
            action: this.handlePdfDownload
          },
          {
            text: 'print',
            icon: 'print',
            action: this.handlePdfPrint
          }
        ]

        if (!this.isCancellationInvoice && !this.isImportedInvoice) {
          items.unshift({
            text: 'duplicate',
            icon: 'file_copy',
            action: this.handleCopy,
            restricted: true
          })
        }

        if (!this.isCommitted) {
          items.unshift({
            text: 'commit',
            icon: 'lock',
            action: this.handleCommit,
            restricted: true
          }, {
            text: 'edit',
            icon: 'edit',
            action: this.handleEdit,
            restricted: true
          })
        }

        return items
      },

      isCanceled () {
        return this.invoice ? (this.invoice.hasOwnProperty('canceled_by') && this.invoice.canceled_by !== null) : false
      },

      isCancellationInvoice () {
        return this.invoice ? this.invoice.cancels_invoice_id !== null : false
      },

      isCommitted () {
        return this.invoice ? this.invoice.committed_at !== null : false
      },

      view: {
        get () {
          return this.isCanceled ? 'invoice' : 'storno'
        },
        set (view) {
          if (view !== this.view) {
            this.handleChangeView()
          }
        }
      },

      activities () {
        const activities = this.invoice.activities ? [ ...this.invoice.activities.data ] : []
        return activities.sort((a, b) => {
          if (a.created_at.date !== b.created_at.date) {
            return a.created_at.date > b.created_at.date ? -1 : 1
          }

          return a.id > b.id ? -1 : 1
        })
      },

      isImportedInvoice () {
        if (!this.invoice) {
          return false
        }

        return this.invoice.type === 'imported_invoice'
      }
    },

    methods: {
      ...mapActions(['setInvoiceStatus', 'commitInvoice']),

      handleCancelInvoice,

      handleMarkInvoiceAsOpen () {
        reopenInvoice(this.invoice.id)
          .then(this.refreshInvoice)
          .catch(createErrorFromResponse)
      },

      handleFullPayment () {
        new InvoiceClient()
          .include(INCLUDES)
          .addPayment(this.invoice.id, { })
          .then(response => {
            this.handleInvoiceUpdate(response.data.data)
            this.$q.notify({ message: this.$t('payment_set_as_paid'), type: 'positive' })
          })
          .catch(createErrorFromResponse)
      },

      handleAddPayment () {
        this.$refs.invoicePaymentModal.open(this.invoice)
      },

      handleDeletePayment (payment) {
        const paymentId = payment.id

        return new InvoiceClient()
          .include(INCLUDES)
          .destroyPayment(this.invoice.id, paymentId)
          .then(response => {
            this.handleInvoiceUpdate(response.data.data)
            this.$q.notify({ message: this.$t('payment_deleted'), type: 'positive' })
          })
      },

      handlePdfDownload () {
        const blob = new Blob([this.pdf], { type: 'application/pdf' })
        FileSaver.saveAs(blob, this.$tc('invoice', 1) + '_' + this.invoice.invoice_number + '.pdf')
      },

      handlePdfPrint () {
        this.$q.loading.show()

        // Load the invoice without the template applied
        new InvoiceClient()
          .pdf(this.invoice.id, false)
          .then(response => {
            this.$q.loading.hide()

            const base64 = btoa(String.fromCharCode(...new Uint8Array(response.data)))
            printJS({ printable: base64, type: 'pdf', base64: true })
          })
          .finally(() => this.$q.loading.hide())
      },

      handleSend () {
        this.$refs.invoiceSendModal.open(this.invoice)
      },

      handleInvoiceUpdate (invoice) {
        this.invoice = invoice
      },

      handleEdit () {
        this.$router.push({
          name: 'edit-invoice',
          params: { teamId: this.activeTeam.id, invoice: this.invoice.id }
        })
      },

      handleCopy () {
        this.$router.push({
          name: 'create-invoice',
          params: { teamId: this.activeTeam.id },
          query: { copy: this.invoice.id }
        })
      },

      handleCommit () {
        this.$q.dialog({
          title: this.$t('confirm_commit_invoice_title'),
          message: this.$t('confirm_commit_invoice_body'),
          ok: this.$t('confirm'),
          cancel: this.$t('abort')
        })
          .then(() => this.performCommit())
          .catch(() => {})
      },

      performCommit () {
        this.commitInvoice(this.invoice.id)
          .then(this.refreshInvoice)
          .then(() => {
            this.$q.notify({ message: this.$t('invoice_committed'), type: 'positive' })
          })
          .catch(createErrorFromResponse)
      },

      refreshInvoice () {
        return (new InvoiceClient())
          .include(INCLUDES)
          .get(this.invoice.id)
          .then(response => {
            this.handleInvoiceUpdate(response.data.data[0])
          })
      },

      handleCreateNote (data) {
        return new InvoiceClient()
          .include('user')
          .addNote(this.invoice.id, data)
          .then(response => {
            const note = response.data.data

            if (this.hasNotes) {
              this.notes.push(note)
            } else {
              const notes = { data: [note] }
              this.$set(this.invoice, 'notes', notes)
            }
          })
      },

      handleUpdateNote (noteId, data) {
        return new NoteClient()
          .include('user')
          .update(noteId, data)
          .then(response => {
            const updatedNote = response.data.data
            const key = this.invoice.notes.data.find(note => {
              return note.id === noteId
            })
            this.invoice.notes.data.splice(key, 1, updatedNote)
          })
      },

      handleDeleteNote (noteId) {
        return new NoteClient()
          .destroy(noteId)
          .then(response => {
            const key = this.invoice.notes.data.find(note => {
              return note.id === noteId
            })
            this.invoice.notes.data.splice(key, 1)
          })
      },

      handleChangeView () {
        let invoiceId = null

        if (this.isCanceled) {
          invoiceId = this.invoice.canceled_by.data.id
        } else {
          invoiceId = this.invoice.cancels_invoice_id
        }

        this.$router.push({ name: 'invoice', params: { team: this.activeTeam.id, invoice: invoiceId } })
      }
    },

    filters: {
      date (value, format) {
        return date.formatDate(parseDate(value), format)
      },

      money (value, currency) {
        return formatMoney(value, currency)
      }
    },

    beforeRouteEnter (to, from, next) {
      const invoiceId = to.params.invoice

      let pdf = new InvoiceClient()
        .pdf(invoiceId)

      let invoice = new InvoiceClient()
        .include(INCLUDES)
        .get(invoiceId)

      Promise.all([pdf, invoice])
        .then(values => {
          next(vm => {
            vm.pdf = values[0].data
            vm.invoice = values[1].data.data[0]
          })
        })
    },

    beforeRouteUpdate (to, from, next) {
      const invoiceId = to.params.invoice

      let pdf = new InvoiceClient()
        .pdf(invoiceId)

      let invoice = new InvoiceClient()
        .include(INCLUDES)
        .get(invoiceId)

      Promise.all([pdf, invoice])
        .then(values => {
          this.pdf = values[0].data
          this.invoice = values[1].data.data[0]
          next()
        })
    },

    components: {
      IconBar,
      NoteInput,
      InvoicePaymentModal,
      InvoiceSendModal,
      StatusChip,
      DocumentViewer,
      Timeline
    }
  }
</script>

<style lang="stylus">
  @import '~variables'

  .invoice__pdf
    width: 100%

    @media (max-width $breakpoint-sm-max)
      width: 100%

  .invoice__delivery-address-entry,
  .invoice__invoice-address-entry
    p
      margin: 0

  .invoice__storno-toggle
    .q-btn.text-dark
      background-color: $dark !important

      > .q-btn-inner
        color: white !important

</style>
